import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MonitorSmartphone, LogOut, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-CH", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

/** Aktive Anmeldungen (Cookie-Sessions) mit Gerät, Datum, IP – einzelne beenden oder alle. */
export default function ActiveSessionsCard() {
  const { logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setSessions((await apiClient.get("/auth/sessions")).data || []); }
    catch { toast.error("Anmeldungen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (s) => {
    const msg = s.current ? "Die aktuelle Anmeldung beenden? Du wirst sofort abgemeldet." : `Anmeldung «${s.device_label}» beenden? Das Gerät muss sich neu anmelden.`;
    if (!window.confirm(msg)) return;
    setBusyId(s.id);
    try {
      const r = await apiClient.delete(`/auth/sessions/${s.id}`);
      if (r.data?.current) { await logout(); window.location.assign("/login"); return; }
      toast.success("Anmeldung beendet");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Beenden fehlgeschlagen"); }
    finally { setBusyId(null); }
  };

  const devices = sessions.filter((s) => !s.api_client || s.current);
  const apiClients = sessions.filter((s) => s.api_client && !s.current);
  const [showApi, setShowApi] = useState(false);

  const revokeMany = async (list, label) => {
    if (!list.length || !window.confirm(`${list.length} ${label} beenden?`)) return;
    setBusyId("many");
    try {
      await Promise.all(list.map((s) => apiClient.delete(`/auth/sessions/${s.id}`)));
      toast.success(`${list.length} ${label} beendet`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Beenden fehlgeschlagen"); }
    finally { setBusyId(null); }
  };

  const Row = ({ s }) => (
    <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`account-session-${s.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
          {s.device_label}
          {s.current && <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold" data-testid="account-session-current-badge">Dieses Gerät</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Angemeldet: {fmtTime(s.created_at)}</span>
          <span>Zuletzt aktiv: {fmtTime(s.last_seen_at)}</span>
          <span>Gültig bis: {fmtTime(s.expires_at)}</span>
          {s.ip && <span className="text-slate-400">IP: {s.ip}</span>}
        </div>
      </div>
      <Button variant="ghost" size="sm" disabled={busyId === s.id} onClick={() => revoke(s)}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`account-revoke-session-button-${s.id}`} title="Anmeldung beenden">
        <LogOut className="h-4 w-4 mr-1" />Beenden
      </Button>
    </li>
  );

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="account-sessions-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
            <MonitorSmartphone className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Aktive Anmeldungen</h2>
            <p className="text-sm text-slate-500 mt-0.5">Alle Geräte, auf denen du gerade angemeldet bist. Beendete Anmeldungen verlieren sofort den Zugriff.</p>
          </div>
        </div>
        {sessions.filter((s) => !s.current).length > 0 && (
          <Button variant="outline" size="sm" disabled={busyId === "many"} onClick={() => revokeMany(sessions.filter((s) => !s.current), "andere Anmeldungen")}
            className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="account-revoke-other-sessions-button">
            <ShieldOff className="h-4 w-4 mr-1.5" />{busyId === "many" ? "Beenden …" : "Alle anderen beenden"}
          </Button>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-slate-500" data-testid="account-sessions-loading">Lädt …</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="account-sessions-list">
          {devices.map((s) => <Row key={s.id} s={s} />)}
          {apiClients.length > 0 && (
            <li className="py-3" data-testid="account-sessions-api-group">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700">{apiClients.length} API-/Skript-Anmeldungen (z. B. Tests, curl, Integrationen)</div>
                  <button type="button" className="text-xs text-sky-700 hover:underline mt-0.5" onClick={() => setShowApi((v) => !v)} data-testid="account-sessions-api-toggle">
                    {showApi ? "Ausblenden" : "Anzeigen"}
                  </button>
                </div>
                <Button variant="ghost" size="sm" disabled={busyId === "many"} onClick={() => revokeMany(apiClients, "API-Anmeldungen")}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="account-revoke-api-sessions-button">
                  <LogOut className="h-4 w-4 mr-1" />Alle beenden
                </Button>
              </div>
              {showApi && <ul className="mt-2 divide-y divide-slate-100 border-t border-slate-100">{apiClients.map((s) => <Row key={s.id} s={s} />)}</ul>}
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}
